import { handleUuid, localGetData, nowServerFormat } from "./utils";
import moment from "moment";

export const emptyOis = {
  __typename: "OfficerIncidentSpecific",
  arrivedAtHospitalAt: null,
  clearedAt: null,
  currentOfficerStatus: null,
  dispatchedAt: null,
  enrouteAt: null,
  enrouteToHospitalAt: null,
  fieldCheck: null,
  id: null,
  incidentId: null,
  lastContactedAt: null,
  logCodeId: null,
  officer: null,
  onSceneAt: null,
  primaryOfficer: false,
  secondaryAddress: null,
  statusId: "3",
};

export const createEmptyPerson = (personUuid, personalAttributeUuid) => {
  const localPersonUuid = personUuid || handleUuid();
  const localPersonalAttributeUuid = personalAttributeUuid || handleUuid();
  return {
    __typename: "Person",
    id: localPersonUuid,
    uuid: localPersonUuid,
    validPersonAlerts: null,
    personalAttribute: {
      __typename: "PersonalAttribute",
      id: localPersonalAttributeUuid,
      uuid: localPersonalAttributeUuid,
      dob: null,
      firstName: null,
      homeAddress: null,
      mailingAddress: null,
      lastName: null,
      middleName: null,
      organization: null,
      phone: null,
      suffix: null,
    },
  };
};

export const createEmptyRelationship = (
  relationshipUuid,
  personUuid,
  personalAttributeUuid
) => {
  return {
    id: relationshipUuid,
    person: createEmptyPerson(personUuid, personalAttributeUuid),
    relationshipType: null,
  };
};

export const emptyNicEntry = {
  __typename: "NicEntry",
  id: null,
  cancelled: false,
  incidentId: null,
  mriNumber: null,
  narrative: null,
  nicNumber: null,
};
export const createEmptyNicEntry = incidentId => {
  const nicUuid = handleUuid();
  return {
    __typename: "NicEntry",
    id: nicUuid,
    cancelled: false,
    incidentId: incidentId,
    mriNumber: null,
    narrative: null,
    nicNumber: null,
  };
};

export const createEmptyTicket = (callTimeStr, addressObj, incident) => {
  const ticketUuid = handleUuid();
  const ticketConditionUuid = handleUuid();

  return {
    id: ticketUuid,
    uuid: ticketUuid,
    ticketNumber: null,
    federalViolationCode: null,
    cvHazardFee: null,
    highway: null,
    cdl: null,
    municipality: null,
    ticketDeliveryType: null,
    incident: incident || null,
    issuedTo: null,
    issuedToAddress: null,
    mailingAddressVerified: null,
    officer: null,
    trespassRequestor: null,
    code: null,
    actualSpeed: null,
    postedLimit: null,
    ticketStatus: null,
    currentStatus: 1,
    dispositionPenaltyAmount: null,
    defendantActions: null,
    reliefActType: null,
    reliefActComment: null,
    defendantParent: null,
    deliveredToType: null,
    municipalOrdinance: null,
    operator: null,
    penalty: null,
    vehicle: null,
    perceivedRace: null,
    ticketStopType: null,
    ticketSearchType: null,
    ticketEvidenceType: null,
    ticketOutcomeType: null,
    passengerTicketSearchType: null,
    passengerTicketEvidenceType: null,
    passengerTicketOutcomeType: null,
    seatBeltOccurrence: null,
    accident: null,
    hazmat: null,
    fatality: null,
    commercialVehicle: null,
    relativeCrimeCharge: null,
    narrative: null,
    ticketCondition: {
      id: ticketConditionUuid,
      weatherConditions: null,
      roadConditions: null,
      trafficConditions: null,
      lightingConditions: null,
    },
    uofInstance: null,
    uofInstanceTypes: null,
    uofInjuryDetails: null,
    ticketLocation: addressObj || null,
    violationDate: callTimeStr || null,
    issuedAt: callTimeStr || null,
    trespassUntil: null,
    radarTimeBefore: null,
    radarTimeAfter: null,
    laserTimeBefore: null,
    laserTimeAfter: null,
  };
};

export const createEmptySpeedMeasurement = () => {
  const speedMeasurementUuid = handleUuid();

  return {
    id: speedMeasurementUuid,
    radarIdStr: null,
    radarTuningForksBefore: null,
    radarInternalCalibrationBefore: null,
    radarSpeedometerVerifBefore: null,
    radarDisplayLightsBefore: null,
    radarTuningForksAfter: null,
    radarInternalCalibrationAfter: null,
    radarSpeedometerVerifAfter: null,
    radarDisplayLightsAfter: null,
    laserIdStr: null,
    laserSelfBefore: null,
    laserScopeAlignmentBefore: null,
    laserKnownDistanceBefore: null,
    laserDisplayLightsBefore: null,
    laserSelfAfter: null,
    laserScopeAlignmentAfter: null,
    laserKnownDistanceAfter: null,
    laserDisplayLightsAfter: null,
  };
};

export const createEmptyIncidentReview = (
  incidentReviewId,
  saOris,
  carStop,
  user
) => {
  const reviewId = incidentReviewId ? incidentReviewId : handleUuid();
  return {
    __typename: "IncidentReview",
    id: reviewId,
    uuid: reviewId,
    approvalStatus: {
      __typename: "ApprovalStatus",
      id: "1",
      name: "Pending",
    },
    fulfilled: false,
    incidentDispositionType: null,
    investigatingOfficer: carStop ? user?.officer : null,
    keepInOfficerQueue: false,
    keepInQueue: false,
    nfirsCode: null,
    nibrsReviewed: false,
    reviewComments: null,
    reviewDate: null,
    reviewStatus: {
      __typename: "ReviewStatus",
      id: "1",
      name: "Pending",
    },
    reviewingSupervisor: null,
    sentToRecords: false,
    sentToRecordsAt: null,
    statusDate: null,
    submitToOri: saOris?.[0] || null,
    submittedAt: null,
  };
};

export const createEmptyVis = () => {
  const visUuid = handleUuid();
  const vehicleUuid = handleUuid();
  return {
    __typename: "VehicleIncidentSpecific",
    id: visUuid,
    uuid: visUuid,
    abandoned: false,
    impounded: false,
    operator: null,
    reasonSeized: null,
    suspectDriven: false,
    wrecker: null,
    towingDestination: null,
    usedInCrime: false,
    vehicle: {
      __typename: "Vehicle",
      id: vehicleUuid,
      uuid: vehicleUuid,
      description: null,
      owner: null,
      plateType: null,
      value: null,
      vehicleColor: null,
      vehicleMake: null,
      vehicleModel: null,
      vehiclePlate: null,
      vehicleState: null,
      vehicleVin: null,
      year: null,
      validVehicleAlerts: [],
    },
    vehicleOfInterest: false,
    vehicleRole: null,
  };
};

const addIds = obj => {
  let localUuid = handleUuid();
  let localObj = JSON.parse(JSON.stringify(obj));
  localObj.id = localUuid;
  localObj.uuid = localUuid;
  return localObj;
};

export const createEmptyGenericAttachment = () =>
  addIds({
    attachmentType: null,
    confidential: false,
    dataUrl: null,
    description: null,
    fileName: null,
    lastUpdated: null,
    ownerName: null,
  });

export const createEmptyIncidentNarrative = () =>
  addIds({
    confidential: false,
    employee: null,
    incidentNarrativeType: null,
    narrative: "",
    timestamp: null,
  });

export const createEmptySuspectVictimAffiliation = () => {
  const svaId = handleUuid();
  return {
    id: svaId,
    uuid: svaId,
    __typename: "SuspectVictimAffiliation",
    biasType: null,
    ibrSuspectVictimRelationshipType: null,
    leokaNarrative: null,
    suspect: null,
    svaOri: null,
    victim: null,
    victimLeoAssignment: null,
    victimLeoState: null,
  };
};

export const createEmptyIncidentOffense = () => {
  const offenseId = handleUuid();
  return {
    id: offenseId,
    uuid: offenseId,
    __typename: "IncidentOffense",
    assaultTypes: [],
    campusCode: null,
    committedType: null,
    counts: null,
    criminalActivityTypes: [],
    entryPointType: null,
    exitPointType: null,
    forceNoForceType: null,
    justifiableHomicideType: null,
    locationType: null,
    nibrsViolationType: null,
    numberOfPremisesEntered: null,
    offenseGangAffiliateds: [],
    offenseIbrDesignation: null,
    offenseViolationPrototype: null,
    offenseWeaponTypes: [],
    significantEvent: null,
    suspectVictimAffiliations: [createEmptySuspectVictimAffiliation()],
  };
};

export const createEmptyIncidentDetail = (incidentDetailId, carStop, user) => {
  const detailId = incidentDetailId ? incidentDetailId : handleUuid();
  return {
    __typename: "IncidentDetail",
    id: detailId,
    uuid: detailId,
    alcohol: null,
    audioRecordings: null,
    clothingEvidence: null,
    crimeSceneProcessed: null,
    crisisServiceInvolved: null,
    dcfNotified: null,
    diagrams: null,
    incidentOffenses: [createEmptyIncidentOffense()],
    genericAttachments: [createEmptyGenericAttachment()],
    incidentNarratives: [createEmptyIncidentNarrative()],
    investigatingOfficer: carStop ? user?.officer : null,
    k9: null,
    lprUsed: null,
    mediaSummary: null,
    medicalRelease: null,
    miranda: null,
    nineOneOneCall: null,
    occurredFrom: null,
    occurredTo: null,
    otherPhysicalEvidence: null,
    photosTaken: null,
    physicalEvidenceText: null,
    printsLifted: null,
    searchType: null,
    secondaryCallType: null,
    siuContacted: null,
    specialVictimsUnit: null,
    swabbings: null,
    troFroExists: null,
    videoRecordings: null,
  };
};

export const createEmptyIncidentArrest = user => {
  let incidentArrestUuid = handleUuid();
  return {
    __typename: "IncidentArrest",
    id: incidentArrestUuid,
    uuid: incidentArrestUuid,
    arrestee: null,
    arrestor: user?.officer,
    arrestedAt: null,
    ibrMultipleArrestType: null,
    location: null,
    citedAt: null,
    arresteeWeaponTypes: [],
    ibrArrestType: null,
    ibrClearanceCode: null,
    arrestCharges: [createEmptyArrestCharge()],
    exceptionClearedAt: null,
    ibrJuvenileDisposition: null,
    childUnderEighteen: null,
    printsPhotosNeeded: null,
  };
};

export const createEmptyArrestCharge = () => {
  const chargeUuid = handleUuid();
  return {
    __typename: "ArrestCharge",
    id: chargeUuid,
    uuid: chargeUuid,
    nibrsSelected: false,
    offenseViolationPrototype: null,
    sealed: false,
  };
};

export const createEmptyIncidentProperty = () => {
  const propertyUuid = handleUuid();
  return {
    __typename: "Property",
    id: propertyUuid,
    uuid: propertyUuid,
    tagNumber: null,
    owner: null,
    propertyCode: null,
    ibrPropertyDescription: null,
    make: null,
    description: null,
    value: null,
    custodyChains: [],
    serialNumber: null,
    location: null,
  };
};

export const createEmptyCustodyChain = () => {
  const custodyChainUuid = handleUuid();
  return {
    id: custodyChainUuid,
    uuid: custodyChainUuid,
    fromOfficer: null,
    evidenceLocationSubLocation: null,
    evidenceLocationLocation: null,
    remarks: null,
    transferredAt: null,
    officer: null,
  };
};

export const createEmptyLabAnalysis = () => {
  const labAnalysisUuid = handleUuid();
  return {
    id: labAnalysisUuid,
    uuid: labAnalysisUuid,
    analysisPerformedBy: null,
    analysisRequestType: null,
    narrative: null,
  };
};

export const createEmptyNarrative = user => {
  return addIds({
    confidential: false,
    employee: user?.officer,
    incidentNarrativeType: null,
    narrative: "",
    timestamp: nowServerFormat(),
  });
};

export const createEmptyIncident = (user, carStop, saOris) => {
  const localStoreSpecificSetup =
    localGetData(`specific-setup-${user?.homeOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { classifications, incidentOrigins, incidentPriorities } =
    specificSetupLists || {};
  const incidentUuid = handleUuid();

  const incidentDetailUuid = handleUuid();
  const incidentReviewUuid = handleUuid();

  const now = nowServerFormat();

  function findTrafficClassification(classifications, carStop) {
    try {
      if (carStop && classifications) {
        const trafficClassification = classifications.find(
          c => c.name.trim() === "Traffic Stop" || c.name.trim() === "Traffic"
        );
        return trafficClassification || null;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  const trafficClassification = findTrafficClassification(
    classifications,
    carStop
  );

  return {
    __typename: "Incident",
    id: incidentUuid,
    uuid: incidentUuid,
    incidentFlags: [],
    incidentDetail: createEmptyIncidentDetail(
      incidentDetailUuid,
      carStop,
      user
    ),
    incidentReview: createEmptyIncidentReview(
      incidentReviewUuid,
      saOris,
      carStop,
      user
    ),
    address: { __typename: "Address", name: "" },
    area: null,
    callCancelled: false,
    callTime: now,
    cargoTheft: false,
    classification: trafficClassification,
    commonCallType: null,
    domv: false,
    drugTypes: [],
    drugRelated: false,
    employee: user?.officer,
    inactive: false,
    incidentNumber: `${user?.badge} Offline Incident ${moment()
      .local()
      .format("MM/DD/YY HH:mm:ss")}`,
    incidentOrigin: carStop
      ? incidentOrigins?.find(io => io.name.trim() === "Officer/Radio") || null
      : incidentOrigins?.find(io => io.name.trim() === "Phone") || null,
    incidentPriority:
      incidentPriorities?.find(ip => ip.name.trim() === "In Progress") || null,
    linkedIncidents: [],
    mentalHealthRelated: false,
    narratives: null,
    narrativeSegments: [],
    nicEntries: [createEmptyNicEntry(incidentUuid)],
    officerIncidentSpecifics: [
      createEmptyOfficerIncidentSpecific(user, carStop, incidentUuid, now),
    ],
    opiateBlocker: false,
    ori: { ...user?.ori },
    personIncidentSpecifics: [
      createEmptyPersonIncidentSpecific(null, null, null, user, true),
    ],
    queryReturns: null,
    stateWideEvents: [],
    rollCall: false,
    team: carStop ? user?.team : null,
    newNarrative: "",
    newQueryReturns: "",
  };
};

export const createEmptyPersonIncidentSpecific = (
  pisUuid,
  personUuid,
  personalAttributeUuid,
  user,
  caller = true
) => {
  let localStoreSpecificSetup = {};
  let storageItem = null;

  if (user?.homeOriId) {
    try {
      storageItem = localGetData(`specific-setup-${user?.homeOriId}`);
    } catch (e) {
      console.error(e);
    }
  }

  if (storageItem) {
    localStoreSpecificSetup = storageItem;
  }

  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { incidentInvolvements } = specificSetupLists || {};

  const localPisUuid = pisUuid || handleUuid();
  const localPersonUuid = personUuid || handleUuid();
  const localPersonalAttributeUuid = personalAttributeUuid || handleUuid();

  return {
    __typename: "PersonIncidentSpecific",
    id: localPisUuid,
    uuid: localPisUuid,
    bac: null,
    juvenile: false,
    yoe: false,
    incidentInvolvement:
      user && caller
        ? incidentInvolvements?.find(c => c.name.trim() === "Caller")
        : null,
    person: {
      id: localPersonUuid,
      uuid: localPersonUuid,
      validPersonAlerts: null,
      personalAttribute: {
        __typename: "PersonalAttribute",
        id: localPersonalAttributeUuid,
        uuid: localPersonalAttributeUuid,
        dob: null,
        firstName: null,
        homeAddress: null,
        mailingAddress: null,
        lastName: null,
        middleName: null,
        organization: null,
        phone: null,
        suffix: null,
      },
    },
  };
};

const createEmptyOfficerIncidentSpecific = (
  user,
  carStop,
  incidentUuid,
  time
) => {
  const oisUuid = handleUuid();
  const localCommonSetup = localGetData(`setup`) || {};
  const { setupLists: commonSetupLists } = localCommonSetup || {};
  const { logCodes } = commonSetupLists || {};

  let ois;
  if (carStop) {
    const logCode =
      logCodes?.find(lc => lc.name.trim() === "23/On Scene") || null;
    ois = {
      id: oisUuid,
      uuid: oisUuid,
      secondaryAddress: null,
      currentOfficerStatus: "23/On Scene",
      incidentId: incidentUuid,
      statusId: null,
      primaryOfficer: true,
      enrouteToHospitalAt: null,
      fieldCheck: null,
      logCodeId: logCode?.id,
      logCode,
      onSceneAt: time,
      enrouteAt: time,
      dispatchedAt: time,
      clearedAt: null,
      officer: {
        __typename: "Officer",
        id: user?.id,
        personalAttribute: {
          firstName: user?.firstName,
          lastName: user?.lastName,
          middleName: user?.middleName,
        },
        deptAttribute: {
          badge: user?.badge,
          lastContactedAt: time,
          ori: {
            id: user?.homeOriId,
            abbreviation: user?.homeOriAbbreviation,
            oriName: user?.ori?.oriName,
            municipalityNumber: user?.ori?.municipalityNumber,
          },
          stateIdNumber: user?.stateIdNumber,
          status: "23",
        },
      },
      __typename: "OfficerIncidentSpecific",
    };
  } else {
    ois = {
      incidentId: incidentUuid,
      clearedAt: null,
      currentOfficerStatus: null,
      id: oisUuid,
      uuid: oisUuid,
      logCodeId: null,
      officer: null,
      primaryOfficer: true,
      onSceneAt: null,
      secondaryAddress: null,
      statusId: "3",
      __typename: "OfficerIncidentSpecific",
    };
  }
  return ois;
};
